<template>
  <a-modal
    :open="visible"
    :title="isReissue ? 'EMR Token 재발급' : 'EMR Token 발급'"
    :width="520"
    :confirm-loading="loading"
    :footer="null"
    :zIndex="1050"
    :maskClosable="false"
    @cancel="handleCancel"
  >
    <div class="modal-body">
      <!-- 토큰 발급 전 입력 화면 -->
      <template v-if="!issuedToken">
        <div class="input-section">
          <label>하드웨어 ID (HW ID)<span class="requiredSpan">*</span></label>
          <a-input
            v-model:value="hwId"
            placeholder="등록할 PC의 하드웨어 ID를 입력해주세요 (ex. 00-1A-2B-3C-4D-5E)"
            @keyup.enter="handleSubmit"
          />
          <div class="input-hint">Certilife EMR Tunnel에서 확인된 기기 ID (HW_ID)를 입력해주세요.</div>
        </div>

        <div class="input-section">
          <label>허용 IP (최대 3개)<span class="requiredSpan">*</span></label>
          <div class="ip-list">
            <div v-for="(ip, index) in allowIps" :key="index" class="ip-item">
              <a-input
                v-model:value="allowIps[index]"
                placeholder="123.45.67.89"
                :status="getIpValidationStatus(ip)"
              />
              <a-button @click="removeIp(index)" class="remove-ip-btn">
                삭제
              </a-button>
            </div>
          </div>
          <a-button
            v-if="allowIps.length < 3"
            @click="addIp"
            class="add-ip-btn"
            type="dashed"
            block
          >
            + IP 추가
          </a-button>
          <div class="input-hint">최소 1개 이상의 IP를 등록해야 합니다.</div>
        </div>

        <div v-if="isReissue" class="warning-message">
          <a-alert
            message="주의"
            description="재발급 시 기존 토큰은 즉시 무효화됩니다."
            type="warning"
            show-icon
          />
        </div>
      </template>

      <!-- 토큰 발급 후 결과 화면 -->
      <template v-else>
        <div class="result-section">
          <a-alert
            :message="isReissue ? 'EMR Token 재발급 완료' : 'EMR Token 발급 완료'"
            type="success"
            show-icon
          />

          <div class="token-display">
            <label>발급된 EMR Token</label>
            <div class="token-value-wrapper">
              <a-input
                :value="issuedToken"
                readonly
                class="token-input"
              />
              <a-button @click="copyToken" class="copy-btn">
                복사
              </a-button>
            </div>
          </div>

          <a-alert
            message="보안 주의사항"
            description="이 토큰은 현재 화면에서만 확인 가능합니다. 모달을 닫으면 다시 조회할 수 없으므로 안전한 곳에 저장해주세요."
            type="warning"
            show-icon
            style="margin-top: 16px;"
          />
        </div>
      </template>

      <div class="button-wrap">
        <template v-if="!issuedToken">
          <a-button @click="handleCancel">취소</a-button>
          <a-button
            type="primary"
            :loading="loading"
            @click="handleSubmit"
            class="yellow-button"
          >
            {{ isReissue ? '재발급' : '발급' }}
          </a-button>
        </template>
        <template v-else>
          <a-button type="primary" @click="handleCancel" class="yellow-button">
            확인
          </a-button>
        </template>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from 'ant-design-vue';

// IPv4 형식 검증 정규식
const IPV4_REGEX = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

export default {
  name: 'EmrTokenModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: String,
      default: ''
    },
    isReissue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'success'],
  data() {
    return {
      hwId: '',
      loading: false,
      issuedToken: '',
      allowIps: []
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
      }
    }
  },
  methods: {
    resetForm() {
      this.hwId = '';
      this.issuedToken = '';
      this.loading = false;
      this.allowIps = [];
    },
    handleCancel() {
      this.$emit('update:visible', false);
      if (this.issuedToken) {
        this.$emit('success');
      }
    },
    addIp() {
      if (this.allowIps.length < 3) {
        this.allowIps.push('');
      }
    },
    removeIp(index) {
      this.allowIps.splice(index, 1);
    },
    isValidIp(ip) {
      if (!ip || ip.trim() === '') return true;
      return IPV4_REGEX.test(ip.trim());
    },
    getIpValidationStatus(ip) {
      if (!ip || ip.trim() === '') return '';
      return this.isValidIp(ip) ? '' : 'error';
    },
    validateAllowIps() {
      const filledIps = this.allowIps.filter(ip => ip && ip.trim() !== '');

      // 최소 1개 필수
      if (filledIps.length === 0) {
        return { valid: false, error: '허용 IP를 최소 1개 이상 등록해주세요.' };
      }

      // 각 IP 형식 검증
      for (const ip of filledIps) {
        if (!IPV4_REGEX.test(ip.trim())) {
          return { valid: false, error: `유효하지 않은 IP 형식입니다: ${ip}` };
        }
      }

      // 중복 검증
      const uniqueIps = new Set(filledIps.map(ip => ip.trim()));
      if (uniqueIps.size !== filledIps.length) {
        return { valid: false, error: '중복된 IP가 있습니다.' };
      }

      return { valid: true };
    },
    async handleSubmit() {
      if (!this.hwId.trim()) {
        message.warning('하드웨어 ID를 입력해주세요.');
        return;
      }

      // allowIps 유효성 검사
      const validation = this.validateAllowIps();
      if (!validation.valid) {
        message.warning(validation.error);
        return;
      }

      this.loading = true;
      try {
        const url = this.isReissue
          ? `/companies/emr-tunnel/${this.companyId}/token/reissue`
          : `/companies/emr-tunnel/${this.companyId}/token`;

        const method = this.isReissue ? 'put' : 'post';

        // 빈 IP 제거 후 전송
        const filledIps = this.allowIps
          .filter(ip => ip && ip.trim() !== '')
          .map(ip => ip.trim());

        const requestBody = {
          hwId: this.hwId.trim()
        };

        if (filledIps.length > 0) {
          requestBody.allowIps = filledIps;
        }

        const response = await this.$apiInstance[method](url, requestBody);

        if (response.status === 200 || response.status === 201) {
          this.issuedToken = response.data.emrToken;
          message.success(this.isReissue ? 'EMR Token이 재발급되었습니다.' : 'EMR Token이 발급되었습니다.');
        }
      } catch (error) {
        console.error('EMR Token 발급 실패:', error);
        if (error.response?.status === 400) {
          message.error(error.response?.data?.message || '입력값이 올바르지 않습니다.');
        } else if (error.response?.status === 404) {
          message.error('병원을 찾을 수 없습니다.');
        } else if (error.response?.status === 403) {
          message.error('권한이 없습니다.');
        } else {
          message.error('EMR Token 발급에 실패했습니다.');
        }
      } finally {
        this.loading = false;
      }
    },
    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.issuedToken);
        message.success('토큰이 클립보드에 복사되었습니다.');
      } catch (error) {
        console.error('복사 실패:', error);
        message.error('복사에 실패했습니다.');
      }
    }
  }
};
</script>

<style scoped>
.modal-body {
  padding: 8px 16px 8px 0;
}

.input-section {
  margin-bottom: 16px;
}

.input-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.input-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.requiredSpan {
  color: red;
  margin-left: 4px;
}

.warning-message {
  margin-top: 16px;
}

.result-section {
  margin-bottom: 16px;
}

.token-display {
  margin-top: 16px;
}

.token-display label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.token-value-wrapper {
  display: flex;
  gap: 8px;
}

.token-input {
  flex: 1;
  font-family: monospace;
  font-size: 12px;
}

.copy-btn {
  flex-shrink: 0;
}

.button-wrap {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.yellow-button {
  background-color: #ffc107 !important;
  border-color: #ffc107 !important;
  color: #000 !important;
}

.yellow-button:hover {
  background-color: #ffca2c !important;
  border-color: #ffc720 !important;
}

.ip-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ip-item {
  display: flex;
  gap: 8px;
}

.ip-item .ant-input {
  flex: 1;
}

.remove-ip-btn {
  flex-shrink: 0;
}

.add-ip-btn {
  margin-top: 8px;
}
</style>